/**
 * inventory-schema.ts — ซื้อสินค้า/สต็อก/กระทบยอดต้นทุน (CONTEXT §5.6, T065)
 *
 * กติกา:
 * - ต้นทุนซื้อจริง (accounting cost) = line amounts + freight_in + packaging + service
 *   → แยกจาก standard cost (Management, standard_cost_history) ไม่เขียนทับกัน
 * - cogs_reconciliations เก็บ standard_cogs กับ accounting_cogs คนละช่อง (variance = accounting − standard)
 * - เงิน/ปริมาณคำนวณด้วย Decimal (ห้าม float)
 */
import { z } from 'zod';
import { moneyString, uuid, dateKey } from './schemas.js';
import { toDecimal, addMoney, multiplyMoney, moneyToString } from './money.js';

// ---------------------------------------------------------------------------
// purchases (+ lines)
// ---------------------------------------------------------------------------
export const purchaseLineInput = z.object({
  skuId: uuid.optional(),
  quantity: z.coerce.number(),
  unitCost: moneyString, // ต้นทุนซื้อจริงต่อหน่วย (accounting)
  amount: moneyString.optional(), // ถ้าไม่ส่ง = qty × unitCost
});
export type PurchaseLineInput = z.infer<typeof purchaseLineInput>;

export const purchaseInput = z.object({
  supplierName: z.string().trim().max(300).optional(),
  supplierTaxId: z.string().trim().max(30).optional(),
  purchaseDate: dateKey.optional(),
  documentNo: z.string().trim().max(120).optional(),
  freightIn: moneyString.optional(),
  packagingCost: moneyString.optional(),
  serviceCost: moneyString.optional(),
  currency: z
    .string()
    .trim()
    .length(3)
    .regex(/^[A-Z]{3}$/)
    .optional(),
  lines: z.array(purchaseLineInput).min(1).max(500),
});
export type PurchaseInput = z.infer<typeof purchaseInput>;

export interface PurchaseTotals {
  /** ผลรวมมูลค่า line (qty × unitCost) */
  linesAmount: string;
  /** ต้นทุนซื้อจริงรวม = lines + freight + packaging + service */
  accountingCost: string;
}

/** คำนวณต้นทุนซื้อจริง (accounting) — แยกจาก standard cost */
export function computePurchaseTotals(p: {
  lines: { quantity: number; unitCost: string; amount?: string | undefined }[];
  freightIn?: string | undefined;
  packagingCost?: string | undefined;
  serviceCost?: string | undefined;
}): PurchaseTotals {
  const linesAmount = p.lines.reduce(
    (acc, l) => acc.plus(l.amount !== undefined ? toDecimal(l.amount) : multiplyMoney(l.unitCost, l.quantity)),
    toDecimal('0'),
  );
  const accountingCost = addMoney(
    linesAmount,
    p.freightIn ?? '0',
    p.packagingCost ?? '0',
    p.serviceCost ?? '0',
  );
  return {
    linesAmount: moneyToString(linesAmount),
    accountingCost: moneyToString(accountingCost),
  };
}

// ---------------------------------------------------------------------------
// inventory movements
// ---------------------------------------------------------------------------
export const inventoryMovementType = z.enum([
  'receive',
  'transfer',
  'adjust',
  'damage_loss',
  'giveaway',
  'opening',
  'closing',
]);
export type InventoryMovementType = z.infer<typeof inventoryMovementType>;

export const inventoryMovementInput = z.object({
  skuId: uuid,
  movementType: inventoryMovementType,
  quantity: z.coerce.number(),
  unitCost: moneyString.optional(),
  movementDate: dateKey.optional(),
  warehouse: z.string().trim().max(120).optional(),
  lot: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(200).optional(),
});
export type InventoryMovementInput = z.infer<typeof inventoryMovementInput>;

/**
 * ทิศทางของ movement ต่อยอดคงเหลือ:
 * - receive/opening → +qty (รับเข้า/ยอดต้นงวด)
 * - adjust/transfer → ใช้ qty ตามที่กรอก (มีเครื่องหมายเอง)
 * - damage_loss/giveaway → −qty (ตัดออก)
 * - closing → 0 (เป็น snapshot ปลายงวด ไม่ใช่ delta)
 */
export function signedMovementQty(type: InventoryMovementType, quantity: number | string): string {
  const q = toDecimal(quantity);
  switch (type) {
    case 'receive':
    case 'opening':
    case 'adjust':
    case 'transfer':
      return q.toString();
    case 'damage_loss':
    case 'giveaway':
      return q.negated().toString();
    case 'closing':
      return '0';
  }
}

/** ยอดคงเหลือสุทธิจากรายการ movement (ไม่รวม closing snapshot) */
export function computeStockBalance(
  movements: { movementType: InventoryMovementType; quantity: number | string }[],
): string {
  const bal = movements.reduce(
    (acc, m) => acc.plus(toDecimal(signedMovementQty(m.movementType, m.quantity))),
    toDecimal('0'),
  );
  return bal.toDecimalPlaces(4).toFixed(4);
}

// ---------------------------------------------------------------------------
// inventory counts (variance = counted − system, DB-generated)
// ---------------------------------------------------------------------------
export const inventoryCountInput = z.object({
  skuId: uuid,
  countDate: dateKey.optional(),
  countedQty: z.coerce.number(),
  systemQty: z.coerce.number(),
  warehouse: z.string().trim().max(120).optional(),
});
export type InventoryCountInput = z.infer<typeof inventoryCountInput>;

// ---------------------------------------------------------------------------
// COGS reconciliation (standard vs accounting — แยกช่อง, variance DB-generated)
// ---------------------------------------------------------------------------
export const cogsReconciliationInput = z.object({
  periodYear: z.coerce.number().int().min(2000).max(2100),
  periodMonth: z.coerce.number().int().min(1).max(12).optional(),
  standardCogs: moneyString, // จาก dashboard standard cost
  accountingCogs: moneyString, // จากซื้อจริง/สต็อก
  notes: z.string().trim().max(1000).optional(),
});
export type CogsReconciliationInput = z.infer<typeof cogsReconciliationInput>;

/** variance = accounting − standard (positive = ต้นทุนบัญชีสูงกว่า standard) */
export function computeCogsVariance(standardCogs: string, accountingCogs: string): string {
  return moneyToString(toDecimal(accountingCogs).minus(toDecimal(standardCogs)));
}
